import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import React, { FC, useState } from "react";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { contentSpacingStyles } from "@store/theme/common/mixins";
import { accordionFilterSectionProps } from "@store/theme/common/props/accordionFilter";
import { TYPOGRAPHY } from "@store/theme/design";
/* Poolcorp Custom Code */

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // Poolcorp Custom Code
    filterWrapper?: InjectableCss;
}

/* Poolcorp Custom Code */
const _seeMoreLessLinkProps = {
    css: css`
        ${getProp("theme.link.defaultProps.css")}
        font-size: ${TYPOGRAPHY.size.smaller};
        font-weight: ${TYPOGRAPHY.weight.black};
        margin-top: 1em;
        outline-offset: -${getProp("theme.focus.width")};
    `,
};
/* Poolcorp Custom Code */
export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        ...accordionFilterSectionProps,
        panelProps: {
            ...accordionFilterSectionProps.panelProps,
            css: css`
                ${accordionFilterSectionProps.panelProps?.css}
                max-height: initial !important;
            `,
        },

    },
    filterWrapper: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 350px;
        `,
    },
    checkBoxGroup: {
        css: css`
            ${contentSpacingStyles(".5em", null)}
            color: ${getColor("text.accent")};
        `,
    },
    checkBox: {},
    checkBoxSelected: {},
    seeAllLink: _seeMoreLessLinkProps,
    seeLessLink: _seeMoreLessLinkProps,
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            {/* Poolcorp Custom Code */}
            <StyledWrapper {...styles.filterWrapper}>
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
            </StyledWrapper>
            {/* Poolcorp Custom Code */}
            {facets &&
                facets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("See All")}
                    </Link>
                ))}
        </ManagedAccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
